import FastForwardIcon from "@mui/icons-material/FastForward";
import FastForwardOutlinedIcon from "@mui/icons-material/FastForwardOutlined";

import { RobotoTooltip } from "@/shared/components";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AutoToggle.module.css";

interface AutoToggleProps {
  enabled: boolean;
  onToggle: () => void;
}

/**
 * Toggle for "automatic mode" — when on, chat tools configured to follow
 * this setting execute without asking for per-call confirmation.
 *
 * Rendered as a fast-forward icon + "Auto" label (filled icon + primary
 * tint when on, outlined icon + muted tint when off) so the affordance
 * reads as "skip past the prompts and go" while matching the icon+label
 * shape of the neighbouring Skills button.
 */
export const AutoToggle = ({ enabled, onToggle }: AutoToggleProps) => {
  const tooltip = enabled
    ? "Automatic mode is enabled. AI Chat tools configured to follow this setting will execute without confirmation."
    : "Automatic mode is disabled. AI Chat tools configured to follow this setting will show a confirmation prompt before executing.";

  const Icon = enabled ? FastForwardIcon : FastForwardOutlinedIcon;

  return (
    <RobotoTooltip title={tooltip} placement="top">
      <span>
        <Button
          size="small"
          variant="text"
          color={enabled ? "primary" : "inherit"}
          className={enabled ? styles.buttonEnabled : styles.button}
          onClick={onToggle}
          aria-label="Automatic mode"
          aria-pressed={enabled}
          startIcon={<Icon className={styles.icon} />}
        >
          Auto
        </Button>
      </span>
    </RobotoTooltip>
  );
};
